import { useSensors, useSensor, TouchSensor, MouseSensor, KeyboardSensor, DndContext, defaultDropAnimationSideEffects, defaultDropAnimation, rectIntersection, pointerWithin } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable';
import { useState, useMemo, Fragment } from 'react';
import { ChevronIcon } from '@ui/kit/icons';
import { sentenceInput } from '@utils/component-framework';
import { NoSSR } from '@utils/react';
import { useOpgavekompleksetEntry } from '../../../../state/hook.entry.js';
import { useUpdateTaskEntryMutation } from '../../../../state/mutations/update-task-entry.js';
import { OpgavekompleksetTaskDragOverlay } from '../../../atom.drag-overlay/_drag-overlay.js';
import { wrapper } from './container.input.style.js';
import { SentenceInput } from './input.content.js';
import { DraggableInput } from './input.draggable.js';
import { sequenceKeyboardCoordinates } from './util.keyboard-coordinates.js';
import { orderInputs } from './util.order-inputs.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function InputContainer(props) {
  const [draggedInputId, setDraggedInputId] = useState();
  const update = useUpdateTaskEntryMutation();
  const initialInputEntries = useOpgavekompleksetEntry({
    select: entry => Object.entries(entry?.data.tasks[props.taskEntryKey]?.details ?? {}).map(([key, value]) => {
      const typedValue = sentenceInput.entry.asTypeSafe(value);
      if (typedValue) {
        return {
          nodeId: key,
          ...typedValue
        };
      }
    }).filter(it => !!it)
  });
  const [inputEntries, setInputEntries] = useState(initialInputEntries);
  const inputs = useMemo(() => orderInputs(props.task.content, inputEntries), [inputEntries, props.task.content]);
  const draggableInputs = inputs.filter(it => !it.options.fixed);
  const sensors = useSensors(useSensor(TouchSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(MouseSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sequenceKeyboardCoordinates,
    scrollBehavior: "instant"
  }));
  const handleDragStart = event => {
    setDraggedInputId(event.active.id.toString());
  };
  const handleDragOver = event => {
    if (event.active.id === event.over?.id) {
      return;
    }
    const sourceInputIndex = inputs.findIndex(it => it.id === event.active.id);
    const targetInputIndex = inputs.findIndex(it => it.id === event.over?.id);
    const sourceInput = inputs[sourceInputIndex];
    const targetInput = inputs[targetInputIndex];
    if (!sourceInput || !targetInput) {
      return;
    }
    update(props.taskEntryKey, prev => {
      const next = prev ?? {};

      //
      // STEP: Ensure all draggable inputs have been registered in our entry to
      // avoid shuffling this entry in the future.
      //
      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i];
        if (input && !input.options.fixed && !next[input.id]) {
          next[input.id] = {
            type: "sentence",
            index: i
          };
        }
      }

      //
      // STEP: Shift inputs between source and target forwards or backwards,
      // depending on the direction the input is shifted.
      //
      const dir = sourceInputIndex < targetInputIndex ? "forwards" : "backwards";
      for (const entry of Object.values(next)) {
        const tileEntry = sentenceInput.entry.asTypeSafe(entry);
        if (!tileEntry) {
          continue;
        }
        if (dir === "forwards" && tileEntry.index > sourceInputIndex && tileEntry.index <= targetInputIndex) {
          const prevValidIndex = inputs.slice(0, tileEntry.index).findLastIndex(it => !it.options.fixed);
          tileEntry.index = prevValidIndex;
        }
        if (dir === "backwards" && tileEntry.index < sourceInputIndex && tileEntry.index >= targetInputIndex) {
          const nextValidIndex = inputs.slice(tileEntry.index + 1).findIndex(it => !it.options.fixed) + 1;
          tileEntry.index = tileEntry.index + nextValidIndex;
        }
      }

      //
      // STEP: Finally position our source at its new position.
      //
      next[sourceInput.id] = {
        type: "sentence",
        index: targetInputIndex
      };
      setInputEntries(Object.entries(next).filter(it => !!sentenceInput.entry.asTypeSafe(it[1])).map(([key, value]) => ({
        nodeId: key,
        ...value
      })));
      return next;
    });
  };
  const handleDragEnd = () => {
    setDraggedInputId(undefined);
  };
  const draggedInput = draggableInputs.find(it => it.id === draggedInputId);
  return /*#__PURE__*/jsx(NoSSR, {
    children: /*#__PURE__*/jsx("div", {
      className: wrapper,
      "data-is-drag-active": !!draggedInputId,
      children: /*#__PURE__*/jsxs(DndContext, {
        sensors: sensors,
        collisionDetection: collisionDetector,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragEnd,
        modifiers: [restrictToParentElement],
        children: [/*#__PURE__*/jsx(SortableContext, {
          items: inputs,
          strategy: rectSwappingStrategy,
          children: inputs.map((input, index) => /*#__PURE__*/jsxs(Fragment, {
            children: [input.options.fixed ? /*#__PURE__*/jsx(SentenceInput, {
              node: input,
              taskEntryKey: props.taskEntryKey
            }) : /*#__PURE__*/jsx(DraggableInput, {
              isDraggingItem: draggedInputId === input.id,
              node: input,
              taskEntryKey: props.taskEntryKey
            }), index !== inputs.length - 1 && /*#__PURE__*/jsx(ChevronIcon, {
              height: 8,
              strokeWidth: 3
            })]
          }, input.id))
        }), /*#__PURE__*/jsx(OpgavekompleksetTaskDragOverlay, {
          task: props.task,
          dropAnimation: {
            ...defaultDropAnimation,
            sideEffects: defaultDropAnimationSideEffects({
              styles: {
                active: {
                  opacity: "0.5"
                }
              }
            })
          },
          children: draggedInput && /*#__PURE__*/jsx(SentenceInput, {
            isDraggingItem: true,
            isDragOverlay: true,
            node: draggedInput,
            taskEntryKey: props.taskEntryKey
          }, draggedInput.id)
        })]
      })
    })
  });
}
const collisionDetector = args => {
  if (!args.pointerCoordinates) {
    return rectIntersection(args);
  }
  return pointerWithin(args);
};

export { InputContainer };
//# sourceMappingURL=container.input.js.map
